import { useState } from 'react'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import {
  useScans,
  useScanHistoryStats,
  useStartSeoScan,
  useStartScan,
  useTriggerHealthCheck,
  useActiveScans,
  useBatchScan,
  useRefreshIntel,
  useCancelScan,
  useCancelSeoScan,
  useDossiers,
} from '@/hooks/useApi'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Spinner } from '@/components/ui/spinner'
import { Select } from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { StatsCard } from '@/components/dashboard/StatsCard'
import { ScanFindingsDrawer } from '@/components/scans/ScanFindingsDrawer'
import {
  KIND_LABEL,
  STATUS_LABEL,
  STATUS_VARIANT,
  formatDuration,
  scanTypeLabel,
} from '@/lib/scanStatus'
import {
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  Activity,
  Play,
  ShieldAlert,
  X,
  AlertTriangle,
} from 'lucide-react'
import { relatief } from '@/lib/datum'
import { cn } from '@/lib/utils'
import type { ActiveScan, ScanRow, ScanStatus } from '@/api/client'

type ScanTypeDef = { key: string; label: string; warn?: string }

const SEO_SCAN_TYPES: ScanTypeDef[] = [
  { key: 'onpage', label: 'On-page' },
  { key: 'site_crawl', label: 'Site crawl' },
  { key: 'tech_audit', label: 'Technisch' },
]
const SECURITY_SCAN_TYPES: ScanTypeDef[] = [
  { key: 'zap_baseline', label: 'ZAP baseline' },
  {
    key: 'zap_full',
    label: 'ZAP full',
    warn:
      'Actieve scan: stuurt echte aanvalspayloads (SQLi/XSS/…). Een WAF kan dit als ' +
      'aanval zien (IP-ban) en het kan formulieren versturen. Whitelist eerst de ' +
      'scanner-IP (46.224.249.193) en draai bij voorkeur buiten piekuren / op staging.',
  },
  { key: 'nuclei', label: 'Nuclei' },
]
const ALL_SCAN_TYPES = [...SEO_SCAN_TYPES, ...SECURITY_SCAN_TYPES]
const TYPE_LABEL: Record<string, string> = Object.fromEntries(
  ALL_SCAN_TYPES.map((t) => [t.key, t.label]),
)

function CheckChip({
  checked,
  onClick,
  label,
  warn,
}: {
  checked: boolean
  onClick: () => void
  label: string
  warn?: string
}) {
  return (
    <label
      onClick={onClick}
      title={warn}
      className={cn(
        'flex cursor-pointer select-none items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors',
        checked ? 'border-primary bg-primary/10 text-foreground' : 'border-border hover:bg-muted',
        warn && checked && 'border-amber-400 bg-amber-50 dark:bg-amber-950/30',
      )}
    >
      <input type="checkbox" checked={checked} readOnly className="h-4 w-4 rounded border-input" />
      {label}
      {warn && <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />}
    </label>
  )
}

function ScanHistoryRow({
  scan,
  onSelect,
}: {
  scan: ScanRow
  onSelect: (s: ScanRow) => void
}) {
  return (
    <tr
      onClick={() => onSelect(scan)}
      className="cursor-pointer hover:bg-muted/30"
      title="Bevindingen bekijken"
    >
      <td className="px-3 py-2">
        <div className="font-medium">{scan.dossier_name ?? '—'}</div>
        {scan.dossier_name === null && (
          <div className="truncate text-xs text-muted-foreground">{scan.target_url}</div>
        )}
      </td>
      <td className="px-3 py-2">
        <span className="text-muted-foreground">{KIND_LABEL[scan.kind]}</span>
        <span className="mx-1 text-muted-foreground">·</span>
        {scanTypeLabel(scan.scan_type)}
      </td>
      <td className="px-3 py-2">
        <Badge variant={STATUS_VARIANT[scan.status]}>{STATUS_LABEL[scan.status]}</Badge>
        {scan.status_detail && (
          <div className="mt-0.5 text-xs text-muted-foreground">{scan.status_detail}</div>
        )}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
        {formatDuration(scan.duration_seconds)}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        {scan.score !== null ? (
          <span
            className={cn(
              'font-semibold',
              scan.score >= 75 ? 'text-green-600 dark:text-green-300' : scan.score >= 50 ? 'text-orange-600 dark:text-orange-300' : 'text-red-600 dark:text-red-300',
            )}
          >
            {scan.score}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        {scan.issue_count}
        {(scan.critical_count ?? 0) > 0 && (
          <span className="ml-1 text-xs text-red-600 dark:text-red-300">({scan.critical_count} kritiek)</span>
        )}
      </td>
      <td className="px-3 py-2 text-right text-xs text-muted-foreground">
        {relatief(scan.created_at)}
      </td>
    </tr>
  )
}

export default function Scans() {
  const [statusFilter, setStatusFilter] = useState<ScanStatus | undefined>()
  const [kindFilter, setKindFilter] = useState<'seo' | 'security' | undefined>()
  const [dossierFilter, setDossierFilter] = useState<number | undefined>()
  const [selectedScan, setSelectedScan] = useState<ScanRow | null>(null)
  const [looseUrl, setLooseUrl] = useState('')
  const [looseType, setLooseType] = useState('onpage')
  const [looseMaxPages, setLooseMaxPages] = useState(50)
  const [looseRenderJs, setLooseRenderJs] = useState(false)
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(
    new Set(['onpage', 'site_crawl', 'tech_audit']),
  )
  const [selectedDossiers, setSelectedDossiers] = useState<Set<number>>(new Set())

  const {
    data: scans,
    isLoading,
    refetch,
  } = useScans({ status: statusFilter, kind: kindFilter, dossier_id: dossierFilter })
  const { data: stats } = useScanHistoryStats()
  const triggerHealthCheck = useTriggerHealthCheck()
  const { data: activeScans } = useActiveScans()
  const { data: dossiers } = useDossiers({ active: true })
  const batchScan = useBatchScan()
  const refreshIntel = useRefreshIntel()
  const startSeoScan = useStartSeoScan()
  const startSecurityScan = useStartScan()
  const cancelSeo = useCancelSeoScan()
  const cancelSec = useCancelScan()
  const queryClient = useQueryClient()

  const active = activeScans ?? []
  const list = dossiers ?? []
  const nameById = new Map<number, string>(list.map((d) => [d.id, d.name]))

  const toggle = <T,>(set: Set<T>, val: T): Set<T> => {
    const next = new Set(set)
    next.has(val) ? next.delete(val) : next.add(val)
    return next
  }

  const allSelected = list.length > 0 && selectedDossiers.size === list.length
  const plannedCount = selectedDossiers.size * selectedTypes.size

  const launch = () => {
    if (!plannedCount) return
    batchScan.mutate(
      { dossierIds: [...selectedDossiers], scanTypes: [...selectedTypes] },
      {
        onSuccess: (r) => toast.success(`${r.dispatched} scan(s) gestart`),
        onError: () => toast.error('Starten mislukt'),
      },
    )
  }

  const looseKind: 'seo' | 'security' = SEO_SCAN_TYPES.some((t) => t.key === looseType)
    ? 'seo'
    : 'security'
  const looseWarn = ALL_SCAN_TYPES.find((t) => t.key === looseType)?.warn
  const loosePending = startSeoScan.isPending || startSecurityScan.isPending

  const launchLoose = async () => {
    const url = looseUrl.trim()
    if (!url) return
    try {
      if (looseKind === 'seo') {
        await startSeoScan.mutateAsync({
          target_url: url,
          scan_type: looseType,
          ...(looseType === 'site_crawl' ? { max_pages: looseMaxPages } : {}),
          ...(looseType === 'onpage' && looseRenderJs ? { render_js: true } : {}),
        })
      } else {
        await startSecurityScan.mutateAsync({ target_url: url, scan_type: looseType })
      }
      queryClient.invalidateQueries({ queryKey: ['scans'] })
      toast.success('Scan gestart', { description: `${scanTypeLabel(looseType)} voor ${url}` })
      setLooseUrl('')
    } catch (err) {
      toast.error('Scan starten mislukt', {
        description: err instanceof Error ? err.message : 'Onbekende fout',
      })
    }
  }

  const cancelScan = (s: ActiveScan) => {
    const mut = s.kind === 'security' ? cancelSec : cancelSeo
    mut.mutate(s.id, {
      onSuccess: () => {
        toast.success('Scan geannuleerd')
        queryClient.invalidateQueries({ queryKey: ['dossiers', 'active-scans'] })
      },
      onError: () => toast.error('Annuleren mislukt'),
    })
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Scans</h1>
          <p className="text-muted-foreground">Actieve taken, scans starten en geschiedenis</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => triggerHealthCheck.mutate()}
            disabled={triggerHealthCheck.isPending}
          >
            <Activity className="mr-2 h-4 w-4" />
            Health check
          </Button>
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Vernieuwen
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <StatsCard title="Totaal scans" value={stats?.total ?? 0} />
        <StatsCard title="Geslaagd" value={stats?.success ?? 0} icon={CheckCircle} />
        <StatsCard title="Mislukt" value={stats?.failed ?? 0} icon={XCircle} />
        <StatsCard title="Overgeslagen" value={stats?.skipped ?? 0} icon={Clock} />
      </div>

      {/* Actieve taken */}
      {active.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Actieve taken ({active.length})</CardTitle>
            <CardDescription>Lopende en wachtende scans — ververst automatisch.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {active.map((s: ActiveScan) => (
              <div key={s.id} className="flex items-center gap-3">
                <div
                  className="w-40 shrink-0 truncate text-sm font-medium"
                  title={nameById.get(s.dossier_id)}
                >
                  {nameById.get(s.dossier_id) ?? `#${s.dossier_id}`}
                </div>
                <div className="w-24 shrink-0 text-xs text-muted-foreground">
                  {TYPE_LABEL[s.scan_type] ?? s.scan_type}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                    {s.progress_percent !== null ? (
                      <div
                        className="h-full bg-primary transition-all duration-500"
                        style={{ width: `${s.progress_percent}%` }}
                      />
                    ) : (
                      <div className="h-full w-full animate-pulse bg-primary/50" />
                    )}
                  </div>
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">
                    {s.progress_detail ?? (s.status === 'pending' ? 'Wachten op worker…' : 'Bezig…')}
                  </p>
                </div>
                <div className="w-10 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                  {s.progress_percent !== null ? `${s.progress_percent}%` : ''}
                </div>
                <button
                  onClick={() => cancelScan(s)}
                  title="Scan annuleren"
                  className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Scans starten */}
      <Card>
        <CardHeader>
          <CardTitle>Scans starten</CardTitle>
          <CardDescription>Vink scan-types en dossiers aan en start ze samen.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div>
              <div className="mb-1.5 text-xs font-medium text-muted-foreground">SEO-scans</div>
              <div className="flex flex-wrap gap-2">
                {SEO_SCAN_TYPES.map((t) => (
                  <CheckChip
                    key={t.key}
                    label={t.label}
                    checked={selectedTypes.has(t.key)}
                    onClick={() => setSelectedTypes((s) => toggle(s, t.key))}
                  />
                ))}
              </div>
            </div>
            <div>
              <div className="mb-1.5 text-xs font-medium text-muted-foreground">Security-scans</div>
              <div className="flex flex-wrap gap-2">
                {SECURITY_SCAN_TYPES.map((t) => (
                  <CheckChip
                    key={t.key}
                    label={t.label}
                    warn={t.warn}
                    checked={selectedTypes.has(t.key)}
                    onClick={() => setSelectedTypes((s) => toggle(s, t.key))}
                  />
                ))}
              </div>
            </div>
          </div>

          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">Dossiers</span>
              <button
                onClick={() =>
                  setSelectedDossiers(allSelected ? new Set() : new Set(list.map((d) => d.id)))
                }
                className="text-xs text-primary hover:underline"
              >
                {allSelected ? 'Niets selecteren' : 'Alles selecteren'}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {list.map((d) => (
                <CheckChip
                  key={d.id}
                  label={d.name}
                  checked={selectedDossiers.has(d.id)}
                  onClick={() => setSelectedDossiers((s) => toggle(s, d.id))}
                />
              ))}
              {list.length === 0 && (
                <span className="text-sm text-muted-foreground">Geen actieve dossiers.</span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button onClick={launch} disabled={!plannedCount || batchScan.isPending}>
              <Play className="mr-1.5 h-4 w-4" />
              Start {plannedCount > 0 ? `${plannedCount} ` : ''}scan{plannedCount === 1 ? '' : 's'}
            </Button>
            <span className="text-xs text-muted-foreground">
              {selectedDossiers.size} dossier(s) × {selectedTypes.size} type(s)
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Losse scan */}
      <Card>
        <CardHeader>
          <CardTitle>Losse scan</CardTitle>
          <CardDescription>
            Eén URL, zonder dossier — voor een site die je nog niet als dossier hebt staan.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <label className="mb-1 block text-sm font-medium">URL</label>
              <Input
                placeholder="https://example.com"
                value={looseUrl}
                onChange={(e) => setLooseUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && launchLoose()}
              />
            </div>
            <div className="w-full sm:w-56">
              <label className="mb-1 block text-sm font-medium">Scan type</label>
              <Select value={looseType} onChange={(e) => setLooseType(e.target.value)}>
                <optgroup label="SEO">
                  {SEO_SCAN_TYPES.map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.label}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Security">
                  {SECURITY_SCAN_TYPES.map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.label}
                    </option>
                  ))}
                </optgroup>
              </Select>
            </div>
            {looseType === 'site_crawl' && (
              <div className="w-full sm:w-32">
                <label className="mb-1 block text-sm font-medium">Max. pagina's</label>
                <Input
                  type="number"
                  min={1}
                  max={200}
                  value={looseMaxPages}
                  onChange={(e) => setLooseMaxPages(parseInt(e.target.value) || 50)}
                />
              </div>
            )}
            {looseType === 'onpage' && (
              <div className="flex w-full items-end sm:w-auto">
                <label
                  className="flex cursor-pointer items-center gap-2 text-sm font-medium"
                  title="Rendert de pagina eerst in een headless browser (voor SPA's). Vereist dat JS-rendering globaal aan staat in Instellingen."
                >
                  <input
                    type="checkbox"
                    checked={looseRenderJs}
                    onChange={(e) => setLooseRenderJs(e.target.checked)}
                    className="h-4 w-4 rounded border-input"
                  />
                  JS-rendering
                </label>
              </div>
            )}
            <Button onClick={launchLoose} disabled={loosePending || !looseUrl.trim()} className="shrink-0">
              {loosePending ? <Spinner size="sm" className="mr-2" /> : <Play className="mr-2 h-4 w-4" />}
              Scan starten
            </Button>
          </div>

          {looseWarn && (
            <p className="mt-3 flex items-start gap-2 rounded-md border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-3 text-xs text-amber-800 dark:text-amber-300">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              {looseWarn}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Security intel */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <CardTitle>Security-intel verversen</CardTitle>
              <CardDescription>
                Draai de OSV-cross-check + NVD-rescore nu op je bestaande inventory (i.p.v. de
                nachtelijke run van 07:00/07:30). Alarmeert bij nieuwe critical/KEV + CVSS-escalaties.
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                refreshIntel.mutate(undefined, {
                  onSuccess: () => toast.success('OSV + NVD intel-refresh gestart'),
                  onError: () => toast.error('Verversen mislukt'),
                })
              }
              disabled={refreshIntel.isPending}
            >
              <ShieldAlert className="mr-1.5 h-4 w-4" />
              Nu verversen (OSV + NVD)
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex gap-2">
          {(
            [
              [undefined, 'Alles'],
              ['seo', 'SEO'],
              ['security', 'Security'],
            ] as const
          ).map(([value, label]) => (
            <Button
              key={label}
              variant={kindFilter === value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setKindFilter(value)}
            >
              {label}
            </Button>
          ))}
        </div>

        <div className="h-5 w-px bg-border" />

        <div className="flex flex-wrap gap-2">
          {(
            [
              [undefined, 'Elke status'],
              ['success', STATUS_LABEL.success],
              ['failed', STATUS_LABEL.failed],
              ['running', STATUS_LABEL.running],
              ['pending', STATUS_LABEL.pending],
              ['skipped', STATUS_LABEL.skipped],
            ] as const
          ).map(([value, label]) => (
            <Button
              key={label}
              variant={statusFilter === value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter(value)}
            >
              {label}
            </Button>
          ))}
        </div>

        <Select
          className="w-auto"
          value={dossierFilter === undefined ? '' : String(dossierFilter)}
          onChange={(e) => setDossierFilter(e.target.value ? Number(e.target.value) : undefined)}
        >
          <option value="">Alle dossiers</option>
          {list.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </Select>
      </div>

      {/* Geschiedenis */}
      <Card>
        <CardHeader>
          <CardTitle>Geschiedenis</CardTitle>
          <CardDescription>
            SEO- en security-scans in één lijst. Klik een rij voor de bevindingen.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex h-32 items-center justify-center">
              <Spinner size="lg" />
            </div>
          ) : !scans?.length ? (
            <p className="p-8 text-center text-sm text-muted-foreground">Geen scans gevonden</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50 text-xs text-muted-foreground">
                    <th className="px-3 py-2 text-left font-medium">Dossier</th>
                    <th className="px-3 py-2 text-left font-medium">Type</th>
                    <th className="px-3 py-2 text-left font-medium">Status</th>
                    <th className="px-3 py-2 text-right font-medium">Duur</th>
                    <th className="px-3 py-2 text-right font-medium">Score</th>
                    <th className="px-3 py-2 text-right font-medium">Bevindingen</th>
                    <th className="px-3 py-2 text-right font-medium">Wanneer</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {scans.map((s) => (
                    <ScanHistoryRow key={`${s.kind}-${s.id}`} scan={s} onSelect={setSelectedScan} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>


      {selectedScan && (
        <ScanFindingsDrawer scan={selectedScan} onClose={() => setSelectedScan(null)} />
      )}
    </div>
  )
}
